import { Component, computed, effect, ElementRef, HostBinding, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import { TaxonomyTreeStore } from 'libs/shared/feature-taxonomy-tree/api';
import moment from 'moment';
import { Subscription } from 'rxjs';
import {
  ComponentConverter,
  ConcurrencyService,
  CurrentlyEditedByComponent,
  getOrganisationImage,
  GuidelineService,
  GuidelineViewService,
  MapToUniqueIdPipe,
  RolePermissions,
  TreeStore,
} from 'shared/domain';
import { FlowchartLoaderService } from 'shared/feature-flowchart';
import { TaxonomyBreadcrumbComponent } from 'shared/tt-taxonomy-breadcrumb';
import { BannerComponent, DialogService, StickyHeaderComponent } from 'shared/ui-common';
import { InViewportDirective, NetworkStatusService, OrganisationSettingsService, ReferenceAuthorNamePipe } from 'shared/util-common';

import { CssVariableService } from '@treetop/shared/color-presets';
import { ReferenceNumberingService } from '@treetop/shared/reference-numbering';
import { ResourceTagListComponent } from '@treetop/shared/tt-resource-tags';

import { SmartbookHeaderComponent } from './header/smartbook-header.component';
import { SmartbookReferenceListComponent } from './references/reference-list.component';
import { GuidelineReviewPageService } from '../../guideline-review/guideline-review-page.service';
import { UpdateMessageDialogComponent, UpdateMessageDialogData } from '../update-message-dialog/update-message-dialog.component';
import { ChapterWidgetListComponent } from './widgets/view-widgets/chapter/chapter-list/chapter-widget-list.component';

@Component({
  selector: 'smartbook-content',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.scss'],
  standalone: true,
  imports: [
    TranslocoModule,
    MatTooltipModule,
    ChapterWidgetListComponent,
    SmartbookHeaderComponent,
    SmartbookReferenceListComponent,
    TaxonomyBreadcrumbComponent,
    StickyHeaderComponent,
    InViewportDirective,
    MatIconModule,
    MapToUniqueIdPipe,
    BannerComponent,
    CurrentlyEditedByComponent,
    ResourceTagListComponent,
  ],
  providers: [ReferenceAuthorNamePipe, LegacySidebarService, FlowchartLoaderService, ComponentConverter],
})
export class SmartbookContentComponent implements OnInit, OnDestroy {
  protected guidelineViewService = inject(GuidelineViewService);
  private referenceNumberingService = inject(ReferenceNumberingService);
  private treeStore = inject(TreeStore);
  private taxonomyTreeStore = inject(TaxonomyTreeStore);
  protected guidelineService = inject(GuidelineService);
  protected concurrencyService = inject(ConcurrencyService);
  protected guidelineReviewPageService = inject(GuidelineReviewPageService, { optional: true });
  private dialogService = inject(DialogService);
  private organisationSettingsService = inject(OrganisationSettingsService);
  private cssVariableService = inject(CssVariableService);
  private elementRef = inject(ElementRef);

  private isOnline = inject(NetworkStatusService).isOnline;

  public reloadGuideline = output<void>();

  protected guidelineData = this.guidelineService.guidelineData;
  protected chapters = this.guidelineService.chapters;
  protected disabled = this.concurrencyService.disabled;
  protected refreshGuidelineButtonEnabled = this.concurrencyService.refreshGuidelineButtonEnabled;

  @HostBinding('class.smart-book') public hostCls = true;

  private scrollServiceSubscriber: Subscription | null = null;

  protected isPublicPage = this.guidelineViewService.isPublicPage;
  protected isRevisionDateEnabled = this.guidelineViewService.isRevisionDateEnabled;
  protected soonToExpirePeriod = this.guidelineViewService.soonToExpirePeriod;
  protected isPreviewMode = this.guidelineViewService.isPreviewMode;
  protected isTeaserMode = this.guidelineViewService.isTeaserMode;
  protected allResourceTags = this.guidelineService.allResourceTags;

  protected isHeaderVisible = signal(false);
  protected showStickyHeader = computed(() => !this.isHeaderVisible() && !this.isTeaserMode());

  private guidelineOrganisationSettings = computed(
    () => this.guidelineData()?.organisation?.organisationSettings ?? this.organisationSettingsService.settings(),
  );

  protected organisationImage = computed(() => {
    if (!this.isOnline()) {
      return undefined;
    }

    const organisationId = this.guidelineData()?.organisation?.id;
    if (organisationId) {
      return getOrganisationImage(organisationId);
    }

    return '';
  });

  protected orderedReferencesWithNumber = this.referenceNumberingService.orderedReferences;

  constructor() {
    effect(() => {
      const colorPresets = this.guidelineOrganisationSettings().customisations?.colorPresets ?? [];
      this.cssVariableService.setColorPresets(colorPresets, this.elementRef.nativeElement);
    });
  }

  public ngOnInit(): void {
    if (!this.isPublicPage()) {
      const orgId = this.guidelineData()?.organisationId ?? this.guidelineData()?.organisation?.id;
      this.taxonomyTreeStore.loadTaxonomy(orgId ? { organisationId: orgId } : {});

      this.treeStore.setTreetopTaxonomy();
    }
  }

  public ngOnDestroy(): void {
    this.scrollServiceSubscriber?.unsubscribe();
  }

  protected showUpdateDialog(): void {
    const data = this.guidelineData();
    if (!data) {
      return;
    }

    this.dialogService
      .open<UpdateMessageDialogData>(UpdateMessageDialogComponent, {
        width: 'calc(100% - 40px)',
        maxWidth: 508,
        title: 'SmartbookMessageDialog.LastUpdateTitle',
        data: {
          message: data.updateMessage,
          infoData: {
            version: data.version,
            updatedAt: moment.utc(data.updatedAt).local().format('DD.MM.YYYY HH:mm'),
            publisher: { firstName: data.publisher?.firstName, lastName: data.publisher?.lastName },
          },
          options: {
            hideDescription: true,
          },
        },
        disableClose: false,
        panelClass: 'tt-smartbook-content-update-message_dialog',
      })
      .afterClosed()
      .subscribe(() => this.guidelineViewService.setShowTags(true));
  }

  protected RolePermissions = RolePermissions;
}
